import React, { useMemo } from 'react';
import { formatPlayerName } from '../../utils/playerName';

// Ordered back to front. Rows are laid out only for the buckets that actually
// contain players, so a 3-4-3 and a 4-2-3-1 both read correctly without
// needing per-formation coordinates.
const POSITION_BUCKETS = ['GK', 'DF', 'DM', 'MF', 'AM', 'FW'];

const BUCKET_BY_POSITION_PREFIX = [
    { prefix: 'GK', bucket: 'GK' },
    { prefix: 'DM', bucket: 'DM' },
    { prefix: 'DF', bucket: 'DF' },
    { prefix: 'CB', bucket: 'DF' },
    { prefix: 'LB', bucket: 'DF' },
    { prefix: 'RB', bucket: 'DF' },
    { prefix: 'WB', bucket: 'DF' },
    { prefix: 'AM', bucket: 'AM' },
    { prefix: 'MF', bucket: 'MF' },
    { prefix: 'CM', bucket: 'MF' },
    { prefix: 'LM', bucket: 'MF' },
    { prefix: 'RM', bucket: 'MF' },
    { prefix: 'FW', bucket: 'FW' },
    { prefix: 'ST', bucket: 'FW' },
    { prefix: 'CF', bucket: 'FW' },
    { prefix: 'LW', bucket: 'FW' },
    { prefix: 'RW', bucket: 'FW' }
];

// Home occupies the bottom half, away the mirrored top half.
const HOME_GOAL_LINE_PERCENT = 93;
const AWAY_GOAL_LINE_PERCENT = 7;
const HALF_DEPTH_PERCENT = 38;
const ROW_MIN_X_PERCENT = 16;
const ROW_MAX_X_PERCENT = 84;
const IDEAL_COLUMN_GAP_PERCENT = 24;

const GOOD_RATING_THRESHOLD = 7;
const ELITE_RATING_THRESHOLD = 8;
const MID_RATING_THRESHOLD = 6;
const LOW_RATING_THRESHOLD = 5;

export const getPositionBucket = (rawPosition) => {
    const position = String(rawPosition || '').trim().toUpperCase();
    if (!position) return 'MF';
    const match = BUCKET_BY_POSITION_PREFIX.find((entry) => position.startsWith(entry.prefix));
    return match ? match.bucket : 'MF';
};

// Engine-sourced names arrive underscore-joined ("F._Ronnow"); display them
// with normal spacing.
const getPlayerName = (player) => (
    formatPlayerName(player?.shortName || player?.name || 'Unknown')
);

const getLiveRating = (player) => {
    const liveRating = Number(player?.live_rating ?? player?.liveRating);
    return Number.isFinite(liveRating) ? liveRating : null;
};

const getRatingTone = (rating) => {
    if (rating === null) return null;
    if (rating >= ELITE_RATING_THRESHOLD) return 'elite';
    if (rating >= GOOD_RATING_THRESHOLD) return 'good';
    if (rating >= MID_RATING_THRESHOLD) return 'mid';
    if (rating >= LOW_RATING_THRESHOLD) return 'low';
    return 'poor';
};

/**
 * Builds [{ player, x, y }] for one side.
 */
export const buildPitchPositions = (players, side) => {
    const grouped = new Map();
    (Array.isArray(players) ? players : []).forEach((player) => {
        const bucket = getPositionBucket(player?.position || player?.currentPosition);
        if (!grouped.has(bucket)) grouped.set(bucket, []);
        grouped.get(bucket).push(player);
    });

    const activeBuckets = POSITION_BUCKETS.filter((bucket) => grouped.get(bucket)?.length);
    const rowCount = activeBuckets.length;
    if (rowCount === 0) return [];

    const rowStep = rowCount > 1 ? HALF_DEPTH_PERCENT / (rowCount - 1) : 0;

    return activeBuckets.flatMap((bucket, rowIndex) => {
        const rowPlayers = grouped.get(bucket);
        const y = side === 'home'
            ? HOME_GOAL_LINE_PERCENT - (rowIndex * rowStep)
            : AWAY_GOAL_LINE_PERCENT + (rowIndex * rowStep);

        // A pair of strikers should stand together near the centre circle, not
        // on opposite touchlines: the row band grows with the player count
        // instead of always spanning the full pitch width.
        const fullBandWidth = ROW_MAX_X_PERCENT - ROW_MIN_X_PERCENT;
        const bandWidth = Math.min(fullBandWidth, (rowPlayers.length - 1) * IDEAL_COLUMN_GAP_PERCENT);
        const bandStart = 50 - (bandWidth / 2);
        const columnStep = rowPlayers.length > 1 ? bandWidth / (rowPlayers.length - 1) : 0;

        return rowPlayers.map((player, columnIndex) => ({
            player,
            bucket,
            x: rowPlayers.length === 1 ? 50 : bandStart + (columnIndex * columnStep),
            y
        }));
    });
};

function PitchPlayer({ entry, side, showRatings }) {
    const rating = getLiveRating(entry.player);
    const ratingTone = getRatingTone(rating);

    return (
        <div
            className={`mx-pp mx-pp--${side}`}
            style={{ left: `${entry.x}%`, top: `${entry.y}%` }}
        >
            <span className="mx-pp__dot">
                <span className="mx-pp__shirt icon-t-shirt" aria-hidden="true" />
                {entry.bucket}
                {showRatings && rating !== null && (
                    <span className={`mx-pp__rating${ratingTone === 'good' ? '' : ` mx-pp__rating--${ratingTone}`}`}>
                        {rating.toFixed(1)}
                    </span>
                )}
            </span>
            <span className="mx-pp__name">{getPlayerName(entry.player)}</span>
        </div>
    );
}

/**
 * Both line-ups on a single pitch, away at the top and home at the bottom.
 * Live ratings appear as badges once the engine starts sending them, with
 * colors based on numeric thresholds. Nothing is shown before kick-off.
 */
function MatchLineupsPitch({
    homePlayers = [],
    awayPlayers = [],
    homeBench = [],
    awayBench = [],
    homeTeamName = 'Home',
    awayTeamName = 'Away',
    homeFormation,
    awayFormation,
    showRatings = false
}) {
    const homePositions = useMemo(() => buildPitchPositions(homePlayers, 'home'), [homePlayers]);
    const awayPositions = useMemo(() => buildPitchPositions(awayPlayers, 'away'), [awayPlayers]);

    if (homePositions.length === 0 && awayPositions.length === 0) {
        return (
            <div className="mx-empty" data-testid="match-lineups-empty">
                Line-ups appear here once both teams have submitted a teamsheet.
            </div>
        );
    }

    return (
        <div data-testid="match-lineups-pitch">
            <div className="mx-formline">
                <span>
                    <strong>{homeTeamName}</strong>
                    {homeFormation ? ` ${homeFormation}` : ''}
                </span>
                <span>
                    {awayFormation ? `${awayFormation} ` : ''}
                    <strong>{awayTeamName}</strong>
                </span>
            </div>

            <div className="mx-pitch-wrap">
                <div className="mx-pitch">
                    <span className="mx-pitch__line mx-pitch__halfway" />
                    <span className="mx-pitch__line mx-pitch__circle" />
                    <span className="mx-pitch__line mx-pitch__box-top" />
                    <span className="mx-pitch__line mx-pitch__box-bottom" />
                    <span className="mx-pitch__line mx-pitch__six-top" />
                    <span className="mx-pitch__line mx-pitch__six-bottom" />

                    {awayPositions.map((entry, index) => (
                        <PitchPlayer
                            key={`away-${entry.player?.id ?? index}`}
                            entry={entry}
                            side="away"
                            showRatings={showRatings}
                        />
                    ))}
                    {homePositions.map((entry, index) => (
                        <PitchPlayer
                            key={`home-${entry.player?.id ?? index}`}
                            entry={entry}
                            side="home"
                            showRatings={showRatings}
                        />
                    ))}
                </div>
            </div>

            {[{ name: homeTeamName, bench: homeBench }, { name: awayTeamName, bench: awayBench }]
                .filter((team) => team.bench.length > 0)
                .map((team) => (
                    <div className="mx-card" key={`bench-${team.name}`}>
                        <h5>{team.name} bench</h5>
                        <div className="mx-bench">
                            {team.bench.map((player, index) => (
                                <span className="mx-bench__chip" key={player?.id ?? `bench-${index}`}>
                                    <span className="mx-bench__pos">
                                        {getPositionBucket(player?.position || player?.currentPosition)}
                                    </span>
                                    <span className="mx-bench__name">{getPlayerName(player)}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                ))}
        </div>
    );
}

export default MatchLineupsPitch;
